/**
 * A designed report's layout, in the one shape the server renders from.
 *
 * Report Studio previews rows grouped, sub-totalled and totalled by its design
 * (`ReportConfig`). Excel and PDF are rendered on the server (they are archived
 * there), which until now received only headers and display text — so the file
 * lost the grouping, the totals and the numbers the preview showed.
 *
 * `buildDesignedViewGrid` sends the server what the preview computed: the rows
 * in group order, each numeric cell's number and Excel format, and a layout
 * saying where each group starts and what its subtotal and the grand total read.
 * The server prints that, and builds the same structure itself for a schedule
 * (backend/app/reporting/report_config.py `designed_grid`); both are held to one
 * fixture by reportLayout.parity.test.ts and test_report_design_parity.py.
 */
import type { Loan } from '@/types'
import { COLUMN_MAP } from './columns'
import type { ColumnDef } from './columnTypes'
import { exportCell } from './derivedMark'
import { pctNumFmt } from './helpers'
import { aggregate, formatAggValue, groupLoans } from './reporting'
import { excelNumber } from './reportExport'
import type { AggMethod, ColumnId, ReportConfig } from './types'

const MISSING = '—'

/** reportExport.ts NUM_FMT: the Excel format a total of each unit is written with. */
const TOTAL_NUM_FMT: Record<string, string> = {
  money: '$#,##0',
  pct: '0.0%',
  points: '0.0"%"',
  x: '0.00"x"',
  days: '0"d"',
  int: '0',
  months: '0',
}

/** A format for a number whose column names no unit — valid for the server's format check. */
const FALLBACK_NUM_FMT = '#,##0.##'

export type CellNumber = [number, string | null] | null

export type TotalsBlock = { cells: string[]; numbers: CellNumber[] }

export type ViewGridLayout = {
  groups: Array<{ label: string | null; count: number; subtotal: TotalsBlock | null }>
  total: TotalsBlock | null
}

export type DesignedViewGrid = {
  headers: string[]
  rows: string[][]
  numeric: boolean[]
  numbers: CellNumber[][]
  layout: ViewGridLayout
  title?: string
}

function exportText(raw: string): string {
  return raw === MISSING ? '' : raw
}

/**
 * One totals row as the workbook writes it: every cell's text, and the number
 * (with its Excel format) where a total is a figure Excel can sum. Column one
 * carries the label, with its own figure after a '·' when it is totalled too.
 */
export function totalsBlock(
  loans: Loan[],
  defs: ColumnDef[],
  config: ReportConfig,
  label: string,
): TotalsBlock {
  const cells: string[] = []
  const numbers: CellNumber[] = []
  defs.forEach((def, index) => {
    const method: AggMethod = config.totals[def.id] ?? 'none'
    if (method === 'none') {
      cells.push(index === 0 ? label : '')
      numbers.push(null)
      return
    }
    const value = aggregate(loans, def.id, method)
    const text = exportText(formatAggValue(value, def.id, method))
    if (index === 0) {
      cells.push(`${label} · ${text}`.trim())
      numbers.push(null)
      return
    }
    cells.push(text)
    if (value == null) numbers.push(null)
    else if (method === 'count' || method === 'distinct') numbers.push([value, null])
    else if (def.unit === 'pct') numbers.push([value, pctNumFmt(value)])
    else if (def.unit && TOTAL_NUM_FMT[def.unit]) numbers.push([value, TOTAL_NUM_FMT[def.unit]])
    else numbers.push(null)
  })
  return { cells, numbers }
}

/**
 * The rows of a designed report in print order (the groups end to end) and the
 * layout that cuts them. `loans` are already filtered and sorted.
 */
export function buildReportLayout(
  loans: Loan[],
  columns: ColumnId[],
  config: ReportConfig,
): { rows: Loan[]; layout: ViewGridLayout } {
  const defs = columns.map((id) => COLUMN_MAP.get(id)).filter((def) => def != null)
  const groups = groupLoans(loans, config.groupBy)
  const groupLabel = config.groupBy ? (COLUMN_MAP.get(config.groupBy)?.label ?? '') : ''
  const ordered = groups.flatMap((group) => group.rows)
  return {
    rows: ordered,
    layout: {
      groups: groups.map((group) => ({
        label: config.groupBy && group.key ? `${groupLabel}: ${group.key}` : null,
        count: group.rows.length,
        subtotal:
          config.groupBy && config.showTotals ? totalsBlock(group.rows, defs, config, 'Subtotal') : null,
      })),
      total: config.showTotals ? totalsBlock(ordered, defs, config, 'Total') : null,
    },
  }
}

/**
 * `filters.viewGrid` for a designed report: the on-screen matrix with the
 * numbers and layout the server needs to print it as the Studio shows it.
 * `loans` are already filtered by the design and sorted by it.
 */
export function buildDesignedViewGrid(
  loans: Loan[],
  columns: ColumnId[],
  title: string | undefined,
  config: ReportConfig,
): DesignedViewGrid {
  const defs = columns.map((id) => COLUMN_MAP.get(id)).filter((def) => def != null)
  const { rows: ordered, layout } = buildReportLayout(loans, columns, config)
  return {
    headers: defs.map((def) => def.label),
    // A derived ratio carries its mark into the designed grid's text (UI-02).
    rows: ordered.map((loan) => defs.map((def) => exportCell(def, loan))),
    numeric: defs.map((def) => def.kind === 'number'),
    numbers: ordered.map((loan) =>
      defs.map((def) => {
        const cell = excelNumber(def, loan)
        return cell ? [cell.value, cell.numFmt ?? FALLBACK_NUM_FMT] : null
      }),
    ),
    layout,
    ...(title?.trim() ? { title: title.trim() } : {}),
  }
}
